import type { CountInWindowConfig } from './count-in-window-input'
import { dayWindows } from './windows'

/**
 * Every count-in-a-window surface whose configuration never varies, keyed by the `inputKey` the
 * catalog routes it under.
 *
 * Stated here rather than beside the components built from it because two things read a surface's
 * configuration: the control that authors the count, and the summary a collapsed row reads that
 * count back as. The window vocabulary in particular has to be the same table in both — a summary
 * naming a window from its own copy would keep naming whatever that copy last said long after the
 * control offered something else.
 *
 * A surface that has to read something from its surroundings — a currency — cannot be stated ahead
 * of time and is built per-field instead; see `frequency-window-input`.
 */
export const COUNT_IN_WINDOW_SURFACES: Readonly<Record<string, CountInWindowConfig>> = {
    'page-url-visited': {
        windowOptions: dayWindows(7, 14, 30, 60, 90),
        defaultCount: 1,
        maxValue: 999,
        valueLabel: 'times',
        countLabel: 'Count',
        expression: { kind: 'freeText', label: 'URL contains', summaryLead: 'where the URL contains' },
        tooltip: 'Time frame does not include page visits from the current day.',
    },
    'keyword-clicked': {
        // The one count surface that can look back four months.
        windowOptions: dayWindows(7, 14, 30, 60, 90, 120),
        defaultCount: 1,
        maxValue: 999,
        valueLabel: 'times',
        countLabel: 'Count',
        expression: { kind: 'keyword', label: 'Keyword', placeholder: 'Select a keyword', summaryLead: 'where the keyword is' },
        tooltip: 'Time frame does not include clicks from the current day.',
    },
    'page-tag-visited': {
        windowOptions: dayWindows(7, 14, 30, 60, 90),
        defaultCount: 1,
        maxValue: 999,
        valueLabel: 'times',
        countLabel: 'Count',
        expression: { kind: 'pageTag', label: 'Page tag', placeholder: 'Select a Page Tag', summaryLead: 'where the page tag is' },
        tooltip: 'Time frame does not include page visits from the current day.',
    },
}
